import React, { useState, useEffect } from 'react';
import { Shield, ShieldCheck, ShieldOff, Key, Copy, Check, RefreshCw, AlertTriangle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const TwoFactorSetup = () => {
  const [status2fa, setStatus2fa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/auth/2fa/status');
      setStatus2fa(res.data);
    } catch {
      // Not critical
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    try {
      const res = await api.post('/auth/2fa/setup');
      setSetupData(res.data);
      setShowSetup(true);
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Echec du setup', variant: 'destructive' });
    }
  };

  const handleVerify = async () => {
    if (!verifyCode.trim() || verifyCode.length < 6) {
      toast({ title: 'Erreur', description: 'Entrez le code a 6 chiffres', variant: 'destructive' });
      return;
    }
    setVerifying(true);
    try {
      const res = await api.post('/auth/2fa/verify', { code: verifyCode.trim() });
      setBackupCodes(res.data.backup_codes);
      setStatus2fa({ ...status2fa, enabled: true });
      toast({ title: '2FA active!', description: 'Votre compte est maintenant protege' });
    } catch (err) {
      toast({ title: 'Code invalide', description: err.response?.data?.detail || 'Verifiez le code', variant: 'destructive' });
    } finally {
      setVerifying(false);
    }
  };

  const handleDisable = async () => {
    if (!disablePassword) {
      toast({ title: 'Erreur', description: 'Mot de passe requis', variant: 'destructive' });
      return;
    }
    try {
      await api.post('/auth/2fa/disable', { password: disablePassword });
      setStatus2fa({ ...status2fa, enabled: false });
      setShowDisable(false);
      setDisablePassword('');
      toast({ title: '2FA desactive' });
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Mot de passe incorrect', variant: 'destructive' });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    });
  };

  if (loading) return null;

  return (
    <Card className="p-5 bg-white/10 backdrop-blur-md border-white/20" data-testid="2fa-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {status2fa?.enabled ? (
            <ShieldCheck className="w-5 h-5 text-green-400" />
          ) : (
            <Shield className="w-5 h-5 text-orange-400" />
          )}
          <h3 className="text-lg font-bold text-white">Authentification 2FA</h3>
        </div>
        {status2fa?.enabled ? (
          <span className="px-2.5 py-1 bg-green-500/20 text-green-400 text-xs rounded-full font-medium" data-testid="2fa-status-badge">
            Active
          </span>
        ) : (
          <span className="px-2.5 py-1 bg-orange-500/20 text-orange-400 text-xs rounded-full font-medium" data-testid="2fa-status-badge">
            Desactive
          </span>
        )}
      </div>

      <p className="text-pink-200 text-xs mb-4 leading-relaxed">
        {status2fa?.enabled
          ? "Votre compte est protege par l'authentification a deux facteurs (TOTP). Un code sera demande a chaque connexion."
          : "Protegez votre compte avec l'authentification a deux facteurs. Utilisez Google Authenticator ou Authy."
        }
      </p>

      {status2fa?.enabled ? (
        <Button
          onClick={() => setShowDisable(true)}
          variant="outline"
          className="w-full border-red-400/30 text-red-300 hover:bg-red-500/10 text-sm"
          data-testid="2fa-disable-btn"
        >
          <ShieldOff className="w-4 h-4 mr-1.5" /> Desactiver le 2FA
        </Button>
      ) : (
        <Button
          onClick={handleSetup}
          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-sm"
          data-testid="2fa-enable-btn"
        >
          <Key className="w-4 h-4 mr-1.5" /> Activer le 2FA
        </Button>
      )}

      {/* Setup Dialog */}
      <Dialog open={showSetup} onOpenChange={(open) => { if (!open && !backupCodes) setShowSetup(false); if (!open && backupCodes) { setShowSetup(false); setBackupCodes(null); setSetupData(null); setVerifyCode(''); } }}>
        <DialogContent className="bg-purple-900 border-purple-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-green-400" />
              {backupCodes ? 'Codes de secours' : 'Configurer le 2FA'}
            </DialogTitle>
          </DialogHeader>

          {backupCodes ? (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-yellow-900/30 border border-yellow-500/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-400" />
                  <p className="text-yellow-300 text-sm font-semibold">Conservez ces codes!</p>
                </div>
                <p className="text-yellow-200 text-xs">
                  Ces codes de secours vous permettent de vous connecter si vous perdez votre appareil. Ils ne seront plus affiches.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, i) => (
                  <div key={i} className="p-2 bg-black/30 rounded text-center">
                    <code className="text-white font-mono text-sm">{code}</code>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => copyToClipboard(backupCodes.join('\n'))}
                variant="outline"
                className="w-full border-green-500/30 text-green-300"
              >
                <Copy className="w-4 h-4 mr-1.5" /> Copier les codes
              </Button>
            </div>
          ) : setupData ? (
            <div className="space-y-4 py-2">
              <p className="text-pink-200 text-sm">
                1. Scannez ce QR code avec Google Authenticator ou Authy :
              </p>
              {setupData.qr_code && (
                <div className="flex justify-center">
                  <img src={setupData.qr_code} alt="QR Code 2FA" className="w-48 h-48 rounded-lg bg-white p-2" />
                </div>
              )}
              <div className="flex items-center gap-2">
                <p className="text-pink-200 text-xs flex-1">Ou entrez ce code manuellement :</p>
                <button onClick={() => copyToClipboard(setupData.secret)} className="text-green-400 hover:text-green-300">
                  {copiedSecret ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <code className="block p-2 bg-black/30 rounded text-center text-green-400 font-mono text-sm tracking-wider">
                {setupData.secret}
              </code>
              <p className="text-pink-200 text-sm">
                2. Entrez le code a 6 chiffres affiche dans l'app :
              </p>
              <Input
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className="bg-white/10 border-white/20 text-white text-center text-2xl tracking-[0.5em] font-mono"
                data-testid="2fa-verify-input"
              />
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-pink-400 animate-spin" />
            </div>
          )}

          {!backupCodes && setupData && (
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowSetup(false); setSetupData(null); setVerifyCode(''); }} className="border-white/20 text-white">
                Annuler
              </Button>
              <Button
                onClick={handleVerify}
                disabled={verifying || verifyCode.length < 6}
                className="bg-green-600 hover:bg-green-700"
                data-testid="2fa-verify-btn"
              >
                {verifying ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Verifier
              </Button>
            </DialogFooter>
          )}

          {backupCodes && (
            <DialogFooter>
              <Button
                onClick={() => { setShowSetup(false); setBackupCodes(null); setSetupData(null); setVerifyCode(''); }}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                J'ai sauvegarde mes codes
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={showDisable} onOpenChange={setShowDisable}>
        <DialogContent className="bg-purple-900 border-purple-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Desactiver le 2FA</DialogTitle>
          </DialogHeader>
          <p className="text-pink-200 text-sm">
            Entrez votre mot de passe pour confirmer la desactivation du 2FA.
          </p>
          <Input
            type="password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            placeholder="Mot de passe"
            className="bg-white/10 border-white/20 text-white"
            data-testid="2fa-disable-password"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisable(false)} className="border-white/20 text-white">
              Annuler
            </Button>
            <Button onClick={handleDisable} className="bg-red-600 hover:bg-red-700" data-testid="2fa-disable-confirm">
              Desactiver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TwoFactorSetup;
